/**
 * PANNELLO FOTO EIDOS (Gioiello Sagoma, 12 ott 2026) — sostituisce il semplice caricamento del file "Foto".
 *
 * Il cliente carica la foto (campo standard di ShapeDiver, che resta) e poi la vede sotto la MASCHERA del gioiello
 * scelto (contorno in ottanio, tutto il resto velato), esattamente come la mappa del Gioiello Mappa:
 *  - trascinando la foto con il dito (o il mouse) la sposta;
 *  - pizzicando con due dita (o con la rotellina) la ingrandisce o la riduce;
 *  - quando il gesto finisce, i tre cursori del modello ("Zoom sagoma", "Sposta X", "Sposta Y") vengono aggiornati
 *    in UN calcolo solo (150 ms dopo l'ultimo movimento), e il gioiello si incide di conseguenza.
 * La corrispondenza con il motore (coda_sagoma.py, _trasformazione_foto): a zoom 100 la foto intera sta nel
 * riquadro della zona incisa (78 % del lato), centrata sul baricentro della zona; Sposta X/Y = -100..100 = meta'
 * larghezza/altezza del riquadro. Le maschere (zona incisa e contorno, in mm, y verso il basso) stanno in
 * public/sagoma_forme.json, esportate da Rhino con la libreria del motore.
 *
 * Questo file sta FUORI da src/shared: gli aggiornamenti di ShapeDiver non lo toccano.
 */
import ParameterFileInputComponent from "@AppBuilderLib/entities/parameter/ui/ParameterFileInputComponent";
import {useParameterComponentCommons} from "@AppBuilderLib/entities/parameter/model/useParameterComponentCommons";
import {useShapeDiverStoreParameters} from "@AppBuilderLib/entities/parameter/model/useShapeDiverStoreParameters";
import {useShapeDiverStoreSession} from "@AppBuilderLib/entities/session/model/useShapeDiverStoreSession";
import {IShapeDiverParameter} from "@AppBuilderLib/entities/parameter/config/parameter";
import {
	PropsParameterComponent,
	PropsParameterWrapper,
} from "@AppBuilderLib/entities/parameter/config/propsParameter";
import ParameterLabelComponent from "@AppBuilderLib/entities/parameter/ui/ParameterLabelComponent";
import Icon from "@AppBuilderLib/shared/ui/icon/Icon";
import {Box, Button, FileInput, Text} from "@mantine/core";
import {preparaStencil} from "./stencilFoto";
import {ricordaFotoCliente} from "./fotoCliente";
import {useEffect, useRef, useState} from "react";
import {useShallow} from "zustand/react/shallow";

/** Nome del parametro (file) del modello che questo pannello sostituisce. */
const NOME_PARAMETRO_FOTO = "Foto";
/** I tre cursori del modello mossi dal pannello. */
const NOME_ZOOM = "Zoom sagoma";
const NOME_SX = "Sposta X";
const NOME_SY = "Sposta Y";
/** Foto di esempio del modello (la stessa dell'ingresso U di "Import Bitmap"): si vede finche' il cliente non carica la sua. */
const FOTO_ESEMPIO = "https://eidos-strade.eidos-direzionale.workers.dev/foto/esempio_sagoma.jpg";
/** Come nel motore: a zoom 100 la foto intera sta nel riquadro della zona (78 % del lato). */
const ZOOM_BASE = 0.78;
const ZOOM_MIN = 30;
const ZOOM_MAX = 300;
const OTTANIO = "#134D5D";
/** scritta del campo di caricamento (Davide, 13 ott 2026) al posto di quella di ShapeDiver */
const TESTO_CARICA = "Carica foto";
const TESTO_SCATTA = "Scatta foto";

const MODELLI: Record<string, string> = {
	Collana: "collana",
	Bracciale: "bracciale",
	Anello: "anello",
	"Orecchino a lobo": "lobo",
	"Orecchino pendente": "pendente",
};

/** Una voce di sagoma_forme.json: contorno del gioiello, zona incisa, baricentro e riquadro della zona (mm, y in giu'). */
// zona_cornice (14 ott sera) = la zona della foto quando c'e' una cornice (1,5 mm di cornice + 0,6 di aria piu' dentro)
type Forma = {sagoma: number[][]; zona: number[][]; zona_cornice?: number[][]; centro: number[]; riquadro: number[]};
type Forme = Record<string, Forma>;

/** Legge la voce scelta in un menu del modello (es. "Modello" -> "Bracciale") e si aggiorna quando cambia. */
function useScelta(namespace: string, nome: string): string {
	const {getParameter} = useShapeDiverStoreParameters(
		useShallow((s) => ({getParameter: s.getParameter})),
	);
	const store = getParameter(namespace, nome);
	const leggi = () => {
		if (!store) return "";
		const p = store.getState() as IShapeDiverParameter<string>;
		const v = String(p.state.uiValue ?? "");
		const scelte = (p.definition as {choices?: string[]}).choices;
		if (scelte && scelte.length) {
			const i = parseInt(v, 10);
			return isNaN(i) ? v : (scelte[i] ?? v);
		}
		return v;
	};
	const [valore, setValore] = useState<string>(leggi);
	useEffect(() => {
		if (!store) return;
		setValore(leggi());
		return store.subscribe(() => setValore(leggi()));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [store]);
	return valore;
}

/** Legge un cursore numerico del modello (valore e id del parametro) e si aggiorna quando cambia. */
function useNumero(namespace: string, nome: string, predefinito: number): {valore: number; id: string | undefined} {
	const {getParameter} = useShapeDiverStoreParameters(
		useShallow((s) => ({getParameter: s.getParameter})),
	);
	const store = getParameter(namespace, nome);
	const leggi = () => {
		if (!store) return predefinito;
		const p = store.getState() as IShapeDiverParameter<number | string>;
		const v = parseFloat(String(p.state.uiValue ?? ""));
		return isNaN(v) ? predefinito : v;
	};
	const [valore, setValore] = useState<number>(leggi);
	useEffect(() => {
		if (!store) return;
		setValore(leggi());
		return store.subscribe(() => setValore(leggi()));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [store]);
	const id = store ? (store.getState() as IShapeDiverParameter<number>).definition?.id : undefined;
	return {valore, id};
}

/** La chiave della maschera in sagoma_forme.json a partire dalle scelte del cliente (come nel pannello mappa). */
function chiaveForma(modello: string, forma: string, misura: string, formaAnello: string): string {
	const tipo = MODELLI[modello] || "collana";
	if (tipo === "anello") return `anello|${(formaAnello || "quadrifoglio").toLowerCase()}|`;
	let f = (forma || "").toLowerCase();
	if (!f || f.startsWith("—") || f.startsWith("-")) f = "cerchio";
	const m = tipo === "collana" || tipo === "bracciale" ? (misura || "piccola").toLowerCase() : "";
	return `${tipo}|${f}|${m}`;
}

const limita = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

/** la "base" dell'incisione scritta dal modello nell'uscita dati "Scheda" (sagoma.base): si aggiorna a ogni calcolo */
// (13 ott) "versione" cresce a ogni scheda nuova: serve al pannello per sapere quando la base e' quella della foto
// appena caricata. Oltre all'iscrizione allo store si rilegge ogni 400 ms: quando arrivano le uscite di un calcolo nuovo
// lo store delle sessioni non sempre avvisa (Davide: "la foto nel riquadro si massimizza sul vettoriale precedente").
type BaseSagoma = {scala: number; hx: number; hy: number; versione: number; fotoPx: [number, number] | null};
/** misure (px) della foto come le ha lette il MODELLO (scheda -> sagoma.foto_px): la scala della base e' riferita a queste */
function fotoPxDi(v: number[] | undefined): [number, number] | null {
	const w = Number(v?.[0]), h = Number(v?.[1]);
	return w > 0 && h > 0 ? [w, h] : null;
}
function useBaseSagoma(): BaseSagoma | null {
	const [base, setBase] = useState<BaseSagoma | null>(null);
	const versioneRef = useRef(0);
	useEffect(() => {
		let ultimo = "";
		const leggi = () => {
			try {
				const {sessions} = useShapeDiverStoreSession.getState();
				for (const s of Object.values(sessions)) {
					const outputs = (s as unknown as {outputs?: Record<string, {name?: string; content?: {data?: unknown}[]}>}).outputs ?? {};
					for (const o of Object.values(outputs)) {
						if (o.name !== "Scheda") continue;
						const d = o.content?.[0]?.data;
						const testo = typeof d === "string" ? d : JSON.stringify(d ?? null);
						if (testo === ultimo) return;
						ultimo = testo;
						const j = (typeof d === "string" ? JSON.parse(d) : d) as {sagoma?: {foto_px?: number[]; base?: {scala_mm_px?: number; centro_hatch_px?: number[]}}} | null;
						const b = j?.sagoma?.base;
						if (b && typeof b.scala_mm_px === "number" && b.scala_mm_px > 0 && Array.isArray(b.centro_hatch_px)) {
							versioneRef.current += 1;
							setBase({scala: b.scala_mm_px, hx: Number(b.centro_hatch_px[0]) || 0, hy: Number(b.centro_hatch_px[1]) || 0, versione: versioneRef.current, fotoPx: fotoPxDi(j?.sagoma?.foto_px)});
						} else setBase(null);
						return;
					}
				}
			} catch {
				/* ignora */
			}
		};
		leggi();
		const disiscrivi = useShapeDiverStoreSession.subscribe(leggi);
		const timer = window.setInterval(leggi, 400);
		return () => {
			disiscrivi();
			window.clearInterval(timer);
		};
	}, []);
	return base;
}

/** riquadro [minx, miny, maxx, maxy] di un poligono */
function riquadroDi(poly: number[][]): [number, number, number, number] {
	let a = Infinity, b = Infinity, c = -Infinity, d = -Infinity;
	for (const [x, y] of poly) {
		if (x < a) a = x;
		if (y < b) b = y;
		if (x > c) c = x;
		if (y > d) d = y;
	}
	return [a, b, c, d];
}

function PannelloFoto(props: PropsParameterComponent) {
	const {state, disabled, handleChange, definition} = useParameterComponentCommons<File | string>(props, 0);
	// (13 ott 2026, Davide: «il cliente deve solo postare la foto») la foto scelta viene preparata come STENCIL (bianco e
	// nero, ombra corretta, pulita, ritagliata: stencilFoto.ts) e al modello arriva solo quella. Se il browser non riesce
	// a leggerla, parte la foto com'e'.
	const [preparo, setPreparo] = useState(false);
	// telefono o tablet (schermo a tocco): si mostra anche "Scatta foto"
	const fotocameraRef = useRef<HTMLInputElement>(null);
	const [telefono] = useState(() => {
		try {
			return window.matchMedia("(pointer: coarse)").matches;
		} catch {
			return false;
		}
	});
	const scegliFoto = async (f: File | null) => {
		if (!f) {
			ricordaFotoCliente(null, null);
			handleChange("", 0);
			return;
		}
		setPreparo(true);
		try {
			const formati = (definition?.format ?? []) as string[];
			const png = !formati.length || formati.some((m) => /png/i.test(m));
			const r = await preparaStencil(f, png ? "image/png" : "image/jpeg");
			// (14 ott) per "Salva tagli": la foto com'era e quella preparata
			ricordaFotoCliente(f, r ? r.file : null);
			handleChange(r ? r.file : f, 0);
		} catch {
			ricordaFotoCliente(f, null);
			handleChange(f, 0);
		} finally {
			setPreparo(false);
		}
	};
	const ns = props.namespace;
	const modello = useScelta(ns, "Modello");
	const forma = useScelta(ns, "Forma");
	const misura = useScelta(ns, "Misura");
	const formaAnello = useScelta(ns, "Forma anello");
	const cornice = useScelta(ns, "Cornice").toLowerCase();
	const conCornice = !!cornice && cornice !== "nessuna";
	const specchia = useScelta(ns, "Specchia la sagoma").toLowerCase().startsWith("s");
	const zoomP = useNumero(ns, NOME_ZOOM, 100);
	const sxP = useNumero(ns, NOME_SX, 0);
	const syP = useNumero(ns, NOME_SY, 0);
	const chiave = chiaveForma(modello, forma, misura, formaAnello);

	// le maschere (mm), esportate da Rhino
	const [forme, setForme] = useState<Forme | null>(null);
	useEffect(() => {
		let vivo = true;
		fetch("sagoma_forme.json")
			.then((r) => (r.ok ? r.json() : null))
			.then((d) => {
				if (vivo && d) setForme(d as Forme);
			})
			.catch(() => undefined);
		return () => {
			vivo = false;
		};
	}, []);

	// la foto da mostrare: quella scelta dal cliente (il parametro vale un File prima dell'invio) oppure l'esempio
	// (13 ott) la stessa foto (stesso nome, peso e data) non crea un indirizzo nuovo: altrimenti, quando ShapeDiver
	// riscrive il valore dopo il calcolo, o quando la sezione FOTO si richiude e si riapre (il pannello rinasce), il
	// pannello credeva a una foto nuova e restava velato in attesa. Per questo la foto iniziale si legge subito.
	const fotoIniziale = state.uiValue;
	const chiaveFotoRef = useRef(
		fotoIniziale instanceof File && fotoIniziale.size > 0 ? `${fotoIniziale.name}|${fotoIniziale.size}|${fotoIniziale.lastModified}` : "",
	);
	const [urlFoto, setUrlFoto] = useState<string>(() =>
		fotoIniziale instanceof File && fotoIniziale.size > 0 ? URL.createObjectURL(fotoIniziale) : FOTO_ESEMPIO,
	);
	useEffect(() => {
		const v = state.uiValue;
		if (v instanceof File && v.size > 0) {
			const chiave = `${v.name}|${v.size}|${v.lastModified}`;
			if (chiave === chiaveFotoRef.current) return undefined;
			chiaveFotoRef.current = chiave;
			setUrlFoto(URL.createObjectURL(v));
			return undefined;
		}
		if (!v) {
			chiaveFotoRef.current = "";
			setUrlFoto(FOTO_ESEMPIO);
		}
		return undefined;
	}, [state.uiValue]);
	// l'indirizzo della foto precedente si libera quando ne arriva un altro
	useEffect(() => () => {
		if (urlFoto.startsWith("blob:")) URL.revokeObjectURL(urlFoto);
	}, [urlFoto]);
	const [misureFoto, setMisureFoto] = useState<[number, number] | null>(null);
	// se l'immagine arriva dalla cache del browser, "onLoad" puo' non scattare: si leggono le misure anche a mano
	const imgRef = useRef<HTMLImageElement>(null);
	useEffect(() => {
		setMisureFoto(null);
		let tentativi = 0;
		const leggi = () => {
			const im = imgRef.current;
			if (im && im.complete && im.naturalWidth && im.naturalHeight) {
				setMisureFoto([im.naturalWidth, im.naturalHeight]);
				return;
			}
			if (tentativi++ < 40) window.setTimeout(leggi, 250);
		};
		leggi();
	}, [urlFoto]);

	// misura del riquadro sullo schermo
	const riquadroRef = useRef<HTMLDivElement>(null);
	const [misPan, setMisPan] = useState<[number, number]>([0, 0]);
	useEffect(() => {
		const el = riquadroRef.current;
		if (!el) return;
		const leggi = () => setMisPan([el.clientWidth, el.clientHeight]);
		leggi();
		const ro = new ResizeObserver(leggi);
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	// valori "vivi" durante il gesto (anteprima immediata); i cursori del modello si aggiornano a gesto finito
	const [vivo, setVivo] = useState({zoom: zoomP.valore, sx: sxP.valore, sy: syP.valore});
	const vivoRef = useRef(vivo);
	vivoRef.current = vivo;
	useEffect(() => {
		setVivo({zoom: zoomP.valore, sx: sxP.valore, sy: syP.valore});
	}, [zoomP.valore, sxP.valore, syP.valore]);
	const timerInvio = useRef<number | undefined>(undefined);
	const idsRef = useRef({zoom: zoomP.id, sx: sxP.id, sy: syP.id});
	idsRef.current = {zoom: zoomP.id, sx: sxP.id, sy: syP.id};
	// UN SOLO CALCOLO per gesto: i tre cursori partono insieme 150 ms dopo l'ultimo movimento
	const manda = () => {
		if (timerInvio.current !== undefined) window.clearTimeout(timerInvio.current);
		timerInvio.current = window.setTimeout(() => {
			timerInvio.current = undefined;
			const v = vivoRef.current;
			const ids = idsRef.current;
			const valori: Record<string, unknown> = {};
			if (ids.zoom) valori[ids.zoom] = Math.round(v.zoom);
			if (ids.sx) valori[ids.sx] = Math.round(v.sx);
			if (ids.sy) valori[ids.sy] = Math.round(v.sy);
			if (!Object.keys(valori).length) return;
			try {
				void useShapeDiverStoreParameters.getState().batchParameterValueUpdate({[ns]: valori}, true, true);
			} catch {
				/* ignora */
			}
		}, 150);
	};

	// ---- la "base" calcolata dal modello (v7, scheda -> sagoma.base): a zoom 100 l'HATCH e' il piu' grande possibile
	// dentro la forma, centrato; qui serve per mostrare la foto nel punto giusto. (Il ritorno a zoom 100 / sposta 0
	// quando cambia la forma lo fa EidosVista, che e' sempre montato: il pannello sul telefono no.)
	const base = useBaseSagoma();
	// (13 ott) foto nuova: finche' il modello non manda la base calcolata sulla NUOVA foto, quella che c'e' e' della foto
	// precedente -> la foto resta velata e si ridisegna appena arriva la scheda nuova (al massimo 15 s)
	const baseRef = useRef(base);
	baseRef.current = base;
	const primaFoto = useRef(true);
	const [versioneVecchia, setVersioneVecchia] = useState<number | null>(null);
	useEffect(() => {
		if (primaFoto.current) {
			primaFoto.current = false;
			return;
		}
		setVersioneVecchia(baseRef.current ? baseRef.current.versione : -1);
		const t = window.setTimeout(() => setVersioneVecchia(null), 15000);
		return () => window.clearTimeout(t);
	}, [urlFoto]);
	// in attesa anche se la base e' di una foto con proporzioni diverse da quella mostrata (scheda non ancora aggiornata)
	const proporzioniDiverse = (() => {
		if (!base?.fotoPx || !misureFoto) return false;
		const a = base.fotoPx[0] / base.fotoPx[1], b = misureFoto[0] / misureFoto[1];
		return Math.abs(a / b - 1) > 0.02;
	})();
	const inAttesa = (versioneVecchia !== null && (!base || base.versione === versioneVecchia)) || proporzioniDiverse;

	// ---- geometria: il gioiello (contorno) riempie il riquadro in altezza o larghezza (Davide, 12 ott sera:
	// "massimizza la maschera"); il centro del riquadro e' il centro del contorno, la foto si riferisce al baricentro
	// della zona incisa come nel motore
	const [w, h] = misPan;
	const f = forme?.[chiave];
	const sagBox = f ? riquadroDi(f.sagoma) : null;
	const ppm = f && sagBox && w && h ? Math.min(w / ((sagBox[2] - sagBox[0]) * 1.08), h / ((sagBox[3] - sagBox[1]) * 1.08)) : 0;
	const ox = sagBox ? (sagBox[0] + sagBox[2]) / 2 : 0;
	const oy = sagBox ? (sagBox[1] + sagBox[3]) / 2 : 0;
	const cx = f ? f.centro[0] : 0;
	const cy = f ? f.centro[1] : 0;
	const bw = f ? f.riquadro[0] : 1;
	const bh = f ? f.riquadro[1] : 1;
	const aSchermo = (x: number, y: number): [number, number] => [w / 2 + (x - ox) * ppm, h / 2 + (y - oy) * ppm];
	const tracciato = (poly: number[][]) =>
		poly.map(([x, y], i) => {
			const [sx, sy] = aSchermo(x, y);
			return `${i ? "L" : "M"}${sx.toFixed(1)} ${sy.toFixed(1)}`;
		}).join(" ") + " Z";
	const dSagoma = f && ppm ? tracciato(f.sagoma) : "";
	const dZona = f && ppm ? tracciato(conCornice && f.zona_cornice ? f.zona_cornice : f.zona) : "";
	// la foto: a zoom 100 sta nel riquadro della zona (78 %), centro = baricentro + spostamento (y del modello verso l'alto)
	let stileFoto: React.CSSProperties = {display: "none"};
	if (f && ppm && misureFoto) {
		const aspetto = misureFoto[0] / misureFoto[1];
		let largMm: number, altMm: number, xMm: number, yGiu: number;
		if (base) {
			// stessa regola del modello v7: scala = base x zoom/100 (mm per pixel della foto intera); il centro
			// dell'hatch va sul baricentro della zona + spostamento (la y del modello e' verso l'alto: qui in giu')
			// (13 ott) i pixel sono quelli della foto come la legge il modello (foto_px), non quelli del browser
			const s = base.scala * (vivo.zoom / 100);
			const px = base.fotoPx ?? misureFoto;
			largMm = px[0] * s;
			altMm = px[1] * s;
			xMm = cx + (vivo.sx / 100) * (bw / 2) - base.hx * s;
			yGiu = cy - (vivo.sy / 100) * (bh / 2) + base.hy * s;
		} else {
			// regola vecchia (modelli fino alla v6): a zoom 100 la foto intera sta nel riquadro della zona al 78 %
			largMm = ZOOM_BASE * (vivo.zoom / 100) * Math.min(bw, bh * aspetto);
			altMm = largMm / aspetto;
			xMm = cx + (vivo.sx / 100) * (bw / 2);
			yGiu = cy - (vivo.sy / 100) * (bh / 2);
		}
		const [px, py] = aSchermo(xMm, yGiu);
		const lp = largMm * ppm;
		const ap = altMm * ppm;
		stileFoto = {
			position: "absolute",
			left: px - lp / 2,
			top: py - ap / 2,
			width: lp,
			height: ap,
			transform: specchia ? "scaleX(-1)" : undefined,
			opacity: inAttesa ? 0.25 : 1,
			transition: "opacity 0.2s",
		};
	}

	// ---- gesti: un dito (o il mouse) sposta, due dita ingrandiscono, la rotellina ingrandisce
	const dita = useRef<Map<number, [number, number]>>(new Map());
	const pxToSx = (dx: number) => (ppm && bw ? (dx / ppm / (bw / 2)) * 100 : 0);
	const pxToSy = (dy: number) => (ppm && bh ? (dy / ppm / (bh / 2)) * 100 : 0);
	const onDown = (e: React.PointerEvent<HTMLDivElement>) => {
		if (disabled) return;
		try {
			(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
		} catch {
			/* puntatore non catturabile (eventi sintetici): si segue lo stesso */
		}
		dita.current.set(e.pointerId, [e.clientX, e.clientY]);
	};
	const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
		const prima = dita.current.get(e.pointerId);
		if (!prima) return;
		const tutte = [...dita.current.entries()];
		if (tutte.length === 1) {
			const dx = e.clientX - prima[0];
			const dy = e.clientY - prima[1];
			setVivo((v) => ({...v, sx: limita(v.sx + pxToSx(dx), -100, 100), sy: limita(v.sy - pxToSy(dy), -100, 100)}));
		} else if (tutte.length >= 2) {
			const [idA, a] = tutte[0];
			const [idB, b] = tutte[1];
			const nuovoA: [number, number] = idA === e.pointerId ? [e.clientX, e.clientY] : a;
			const nuovoB: [number, number] = idB === e.pointerId ? [e.clientX, e.clientY] : b;
			const dPrima = Math.hypot(a[0] - b[0], a[1] - b[1]) || 1;
			const dDopo = Math.hypot(nuovoA[0] - nuovoB[0], nuovoA[1] - nuovoB[1]) || 1;
			const dx = (nuovoA[0] + nuovoB[0] - a[0] - b[0]) / 2;
			const dy = (nuovoA[1] + nuovoB[1] - a[1] - b[1]) / 2;
			setVivo((v) => ({
				zoom: limita(v.zoom * (dDopo / dPrima), ZOOM_MIN, ZOOM_MAX),
				sx: limita(v.sx + pxToSx(dx), -100, 100),
				sy: limita(v.sy - pxToSy(dy), -100, 100),
			}));
		}
		dita.current.set(e.pointerId, [e.clientX, e.clientY]);
	};
	const onUp = (e: React.PointerEvent<HTMLDivElement>) => {
		if (!dita.current.has(e.pointerId)) return;
		dita.current.delete(e.pointerId);
		if (dita.current.size === 0) manda();
	};
	const onWheel = (e: React.WheelEvent<HTMLDivElement>) => {
		if (disabled) return;
		e.preventDefault();
		const fattore = Math.exp(-e.deltaY * 0.0015);
		setVivo((v) => ({...v, zoom: limita(v.zoom * fattore, ZOOM_MIN, ZOOM_MAX)}));
		manda();
	};
	// la rotellina va fermata "non passiva", altrimenti la pagina scorre: si registra a mano
	useEffect(() => {
		const el = riquadroRef.current;
		if (!el) return;
		const blocca = (ev: WheelEvent) => ev.preventDefault();
		el.addEventListener("wheel", blocca, {passive: false});
		return () => el.removeEventListener("wheel", blocca);
	}, []);

	return (
		<Box style={{opacity: disabled ? 0.6 : 1}}>
			<ParameterLabelComponent {...props} />
			{/* (13 ott 2026) campo nostro: "Carica foto" con la freccia; la foto passa dalla preparazione automatica */}
			{/* (14 ott 2026, Davide) sul cellulare, SOTTO "Carica foto", anche "Scatta foto": apre direttamente la fotocamera
			    (capture), la foto scattata passa dalla stessa preparazione automatica */}
			<Box>
				<FileInput
					placeholder={TESTO_CARICA}
					accept="image/*"
					clearable={!!state.execValue}
					leftSection={<Icon iconType={"tabler:upload"} />}
					leftSectionPointerEvents="none"
					disabled={disabled || preparo}
					value={state.uiValue instanceof File ? state.uiValue : null}
					onChange={(f) => void scegliFoto(f)}
				/>
				{telefono && (
					<>
						<Button
							variant="outline"
							color={OTTANIO}
							leftSection={<Icon iconType={"tabler:camera"} />}
							disabled={disabled || preparo}
							onClick={() => fotocameraRef.current?.click()}
							fullWidth
							mt={8}
						>
							{TESTO_SCATTA}
						</Button>
						<input
							ref={fotocameraRef}
							type="file"
							accept="image/*"
							capture="environment"
							style={{display: "none"}}
							onChange={(e) => {
								const f = e.currentTarget.files?.[0] ?? null;
								e.currentTarget.value = "";
								if (f) void scegliFoto(f);
							}}
						/>
					</>
				)}
			</Box>
			<Text size="xs" c="dimmed" mt={6} mb={4} className="eidos-mappa-aiuto" style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
				{preparo ? "Preparo la foto per l'incisione…" : "Trascina la foto per spostarla, pizzica per ingrandirla."}
			</Text>
			<div
				ref={riquadroRef}
				className="eidos-mappa-riquadro eidos-foto-riquadro"
				style={{position: "relative", height: 260, marginTop: 4, border: "1px solid #d9d4cd"}}
				onPointerDown={onDown}
				onPointerMove={onMove}
				onPointerUp={onUp}
				onPointerCancel={onUp}
				onWheel={onWheel}
			>
				<img
					ref={imgRef}
					src={urlFoto}
					alt=""
					draggable={false}
					style={stileFoto}
					onLoad={(e) => {
						const im = e.currentTarget;
						if (im.naturalWidth && im.naturalHeight) setMisureFoto([im.naturalWidth, im.naturalHeight]);
					}}
				/>
				<svg style={{position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none"}}>
					{/* velo invertito: la zona incisa resta pulita, tutto il resto e' velato */}
					<path d={w && h && dZona ? `M0 0 H${w} V${h} H0 Z ${dZona}` : ""} fill="rgba(19,77,93,0.14)" fillRule="evenodd" stroke="none" />
					<path d={dZona} fill="none" stroke={OTTANIO} strokeWidth={1} strokeDasharray="4 3" />
					<path d={dSagoma} fill="none" stroke={OTTANIO} strokeWidth={2} strokeLinejoin="round" />
				</svg>
			</div>
		</Box>
	);
}

/**
 * Componente per i parametri di tipo file: "Foto" diventa il pannello con la foto sotto la maschera,
 * tutti gli altri restano il caricamento standard di ShapeDiver.
 */
export default function EidosFotoComponent(
	props: PropsParameterComponent & Partial<PropsParameterWrapper>,
) {
	const {definition} = useParameterComponentCommons<File | string>(props, 0);
	const nome = definition?.displayname || definition?.name || "";
	if (nome === NOME_PARAMETRO_FOTO) {
		return <PannelloFoto {...props} />;
	}
	return <ParameterFileInputComponent {...props} />;
}
